import type { ReactElement, ReactNode, JSX } from "react";
import type { Options as RenderOptions } from "@contentful/rich-text-react-renderer";
import type { Block, Inline, Node } from "@contentful/rich-text-types";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { space } from "@ecco/ui/tokens";
import { H } from "@ecco/ui/a11y-heading";
import { Text } from "@ecco/ui/text";
import { Flex } from "@ecco/ui/flex";
import { List, ListItem, OrderedList, UnorderedList } from "@ecco/ui/list";
import { Divider } from "@ecco/ui/divider";
import { LinkBox } from "@ecco/ui/link-overlay";
import { Box } from "@ecco/ui/box";
import { NextImage } from "@ecco/ui/next-image";
import type { Replacements } from "@lib/utils/interpolateText";
import { interpolateText } from "@lib/utils/interpolateText";
import type { ILink } from "@contentful-api/types/contentful";
import { getAssetFileUrl } from "@contentful-api/helpers/getAssetFileUrl";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { isECCOIconName } from "@ecco/ui/icon-names";
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    AccordionIcon,
} from "@ecco/ui/accordion";
import { RichText } from "./RichText";
import { accordionItemStyles } from "@components/cms/accordion/AccordionStyles";
import { Link as ContentfulLink } from "@components/cms/link/Link";
import { Link } from "@ecco/ui/link";
import AccessibilityFeedback from "@components/cms/accessibility/AccessibilityFeedback";
import AccessibilityToggle from "@components/cms/accessibility/AccessibilityToggle";

export interface Options {
    replacements?: Replacements;
    variant?: "default" | "accordion";
    alignAccordionWithHeader?: boolean;
}

const richTextDefaultMarginBottom = 3;
const richTextAccordionMarginBottom = space["0.4"];

const richTextDefaultFontSize = [
    "mobileBodyTextNormal",
    "mobileBodyTextNormal",
    "mobileBodyTextNormal",
    "desktopBodyTextNormal",
];
const richTextAccordionFontSize = "desktopBodyTextSmall";

// TODO: add table components, EMBEDDED_ENTRY
export function getOptions(options?: Options): RenderOptions {
    const isAccordion = options?.variant === "accordion";
    const richTextBodyFontSize = isAccordion ? richTextAccordionFontSize : richTextDefaultFontSize;

    return {
        renderMark: {
            [MARKS.BOLD]: (text: ReactNode): JSX.Element => (
                <Text as="strong" fontWeight="bold" fontSize={"inherit"}>
                    {text}
                </Text>
            ),
            [MARKS.UNDERLINE]: (text: ReactNode): JSX.Element => (
                <Text as="span" textDecoration="underline" fontSize={"inherit"}>
                    {text}
                </Text>
            ),
            [MARKS.ITALIC]: (text: ReactNode): JSX.Element => (
                <Text as="em" fontStyle="italic" fontSize={"inherit"}>
                    {text}
                </Text>
            ),
            [MARKS.CODE]: (text: ReactElement<any>): JSX.Element => {
                return (
                    <Text
                        as="span"
                        visibility={"hidden"}
                        // When mark <code> is used within a Paragraph is adding an extra space. So we are trimming it to map the correct id.
                        id={text.props.children.trim()}
                        fontSize={"inherit"}
                    >
                        {text}
                    </Text>
                );
            },
        },
        renderNode: {
            [BLOCKS.PARAGRAPH]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <Text
                    as="p"
                    color="inherit"
                    mb={3}
                    fontSize={richTextBodyFontSize}
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                >
                    {children}
                </Text>
            ),
            [BLOCKS.HEADING_1]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <H mb={[6, 6, 6, 8]} as="h1" fontSize={["mobileHeading1", "desktopHeading1"]}>
                    {children}
                </H>
            ),
            [BLOCKS.HEADING_2]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <H mb={6} as="h2" fontSize={["mobileHeading2", "desktopHeading2"]}>
                    {children}
                </H>
            ),
            [BLOCKS.HEADING_3]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <H mb={6} as="h3" fontSize={["mobileHeading3", "desktopHeading3"]}>
                    {children}
                </H>
            ),
            [BLOCKS.HEADING_4]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <H mb={6} as="h4" fontSize={["mobileHeading4", "desktopHeading4"]}>
                    {children}
                </H>
            ),
            [BLOCKS.HEADING_5]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <H mb={6} as="h5" fontSize={["mobileHeading5", "desktopHeading5"]}>
                    {children}
                </H>
            ),
            [BLOCKS.HEADING_6]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <H mb={6} as="h6" fontSize={["mobileBodyTextLarge", "desktopBodyTextLarge"]}>
                    {children}
                </H>
            ),
            [BLOCKS.HR]: (_node: Inline | Block, _children) => <Divider my={2} />,
            [BLOCKS.UL_LIST]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <List
                    color="black"
                    mb={isAccordion ? richTextAccordionMarginBottom : richTextDefaultMarginBottom}
                    fontSize={richTextBodyFontSize}
                >
                    <UnorderedList fontSize="inherit">{children}</UnorderedList>
                </List>
            ),
            [BLOCKS.OL_LIST]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <List
                    color="black"
                    mb={isAccordion ? richTextAccordionMarginBottom : richTextDefaultMarginBottom}
                    fontSize={richTextBodyFontSize}
                >
                    <OrderedList fontSize="inherit">{children}</OrderedList>
                </List>
            ),
            [BLOCKS.LIST_ITEM]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                <ListItem>{children}</ListItem>
            ),
            [BLOCKS.QUOTE]: (_node: Inline | Block, children: ReactNode): JSX.Element => (
                // TODO: if need, should be changed by custom quote component
                <Box bg={"gray.100"} borderLeftWidth={4} borderLeftColor={"gray.400"} p={2}>
                    {children}
                </Box>
            ),
            [BLOCKS.EMBEDDED_ASSET]: (node: Inline | Block): JSX.Element => {
                const data = node?.data?.target?.fields;

                return (
                    <Flex
                        display="inline-flex"
                        alignItems="center"
                        mr={2}
                        maxW={`${data?.file?.details?.image?.width}px`}
                        height={`${data?.file?.details?.image?.height}px`}
                    >
                        <NextImage
                            src={data?.file?.url}
                            alt={data?.description}
                            quality={85}
                            objectFit="contain"
                            sizes={["50vw"]}
                        />
                    </Flex>
                );
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node: Inline | Block): JSX.Element => {
                const data = node?.data?.target;
                const accordionHeader = data?.fields?.header;
                const accordionItems = data?.fields?.accordionItems;

                const contents = data?.fields?.text?.content;

                if (contents?.length > 0) {
                    return (
                        <Box>
                            {contents.map((content, index) => (
                                <RichText key={index} data={content} />
                            ))}
                        </Box>
                    );
                }

                return (
                    <Flex direction={"column"}>
                        {accordionHeader && (
                            <H size="h5" textAlign="start" mb={[4, 4, 6]}>
                                {accordionHeader}
                            </H>
                        )}
                        <Accordion
                            w="full"
                            allowMultiple
                            py={5}
                            px={options?.alignAccordionWithHeader ? 0 : 5}
                        >
                            {accordionItems?.map((item) => {
                                return (
                                    <AccordionItem
                                        key={item.sys.id}
                                        {...accordionItemStyles}
                                        py={4}
                                    >
                                        <AccordionButton
                                            py={0}
                                            px={0}
                                            w="100%"
                                            sx={{
                                                // Theme adds padding when open, which makes it jump
                                                "&[aria-expanded='true']": { px: 0, py: 0 },
                                                "&:hover": { bg: "transparent" },
                                            }}
                                            display="flex"
                                            justifyContent={"space-between"}
                                            gap={2}
                                        >
                                            <Box textAlign="left">{item.fields.heading}</Box>
                                            <AccordionIcon />
                                        </AccordionButton>
                                        <AccordionPanel pt={2} pb={0} px={0}>
                                            <RichText data={item.fields.text} />
                                        </AccordionPanel>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    </Flex>
                );
            },
            [INLINES.ENTRY_HYPERLINK]: (node: Node, children: ReactNode): JSX.Element => {
                const link: ILink = node?.data?.target;
                const icon = link?.fields?.icon?.toLocaleLowerCase();

                // If we don't have a valid link, render as a paragraph instead.
                if (!link?.fields) {
                    return (
                        <Text as="span" fontSize={"inherit"}>
                            {children}
                        </Text>
                    );
                }

                if (link?.fields?.modal === "accessibilityFeedback") {
                    return <AccessibilityFeedback />;
                }

                if (link?.fields?.modal === "accessibilityToggle") {
                    return <AccessibilityToggle />;
                }

                const Icon = isECCOIconName(icon) ? (
                    <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        bg="gray.50"
                        color="black"
                        fontSize="inherit"
                        aria-hidden="true"
                    >
                        <ECCOIcon name={icon} />
                    </Box>
                ) : null;

                return (
                    <ContentfulLink
                        link={link}
                        color="inherit"
                        wordBreak="break-word"
                        textAlign="initial"
                        display="inline"
                        variant="underline"
                        fontSize="inherit"
                    >
                        {children}
                        {Icon}
                    </ContentfulLink>
                );
            },
            [INLINES.EMBEDDED_ENTRY]: (node: Node): JSX.Element => {
                const image = node?.data;

                return (
                    <NextImage
                        src={image?.url}
                        width={image?.width}
                        height={image?.height}
                        alt={image?.description}
                        quality={85}
                        sizes={["50vw"]}
                    />
                );
            },
            [INLINES.HYPERLINK]: (node: Node, children: ReactNode): JSX.Element => {
                if (isAccordion) {
                    return (
                        <LinkBox as="span">
                            <Link
                                href={node?.data?.uri?.trim()}
                                color="inherit"
                                fontWeight="bold"
                                textDecoration="underline"
                                textDecorationThickness="1px"
                                textUnderlineOffset="4px"
                                fontSize="inherit"
                                _hover={{ color: "gray.500", textDecorationColor: "gray.500" }}
                            >
                                {children}
                            </Link>
                        </LinkBox>
                    );
                }
                return (
                    <LinkBox as="span">
                        <Link color="black" href={node?.data?.uri?.trim()} fontSize="inherit">
                            {children}
                        </Link>
                    </LinkBox>
                );
            },
            [INLINES.ASSET_HYPERLINK]: (node: Node, children: ReactNode): JSX.Element => {
                const href = getAssetFileUrl(node?.data?.target);

                if (!href) {
                    return (
                        <Text as="span" fontSize={"inherit"}>
                            {children}
                        </Text>
                    );
                }

                if (isAccordion) {
                    return (
                        <LinkBox as="span">
                            <Link
                                href={href}
                                color="inherit"
                                fontWeight="bold"
                                textDecoration="underline"
                                textDecorationThickness="1px"
                                textUnderlineOffset="4px"
                                fontSize="inherit"
                                target="_blank"
                                rel="noopener noreferrer"
                                _hover={{ color: "gray.500", textDecorationColor: "gray.500" }}
                            >
                                {children}
                            </Link>
                        </LinkBox>
                    );
                }

                return (
                    <LinkBox as="span">
                        <Link
                            color="black"
                            href={href}
                            fontSize="inherit"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {children}
                        </Link>
                    </LinkBox>
                );
            },
        },
        renderText: (text: string): JSX.Element => (
            <>{options?.replacements ? interpolateText(text, options?.replacements) : text}</>
        ),
    };
}
